/**
 * Holding an ASSISTED session, and getting back out of it (D34).
 *
 * An operator asks the Passport for a session as somebody (`POST /auth/assist`) and then WEARS it:
 * every request the shell and its modules make carries that token, so the Desk behaves exactly as it
 * does for the person being helped. That is the point — support that cannot see the screen the person
 * is looking at is not support.
 *
 * 🔴 Three rules, and each is here rather than in a comment somewhere else:
 *
 *  1. The operator's OWN session is kept, in this tab, so "stop assisting" is a step back rather than
 *     a sign-out. An operator who has to sign in again to leave will not leave.
 *  2. It is never persisted past the tab. `sessionStorage`, like the session it stands beside — a
 *     remembered assist would come back over an ordinary session, which is a lie in the most alarming
 *     possible direction.
 *  3. Nothing here decides WHETHER an assist is allowed. The Passport does, and it refuses far more
 *     than it allows. This module is the part that makes one visible.
 */
const OPERATOR_TOKEN_KEY = "pinksy:assist:operator-token";
const OPERATOR_REFRESH_KEY = "pinksy:assist:operator-refresh";

function storage(): Storage | null {
  try {
    return window.sessionStorage;
  } catch {
    return null;
  }
}

/** Put the operator's own credentials aside before the assisted ones take their place. */
export function keepOperatorSession(token: string | null, refresh: string | null): void {
  try {
    const store = storage();
    if (store === null) return;
    if (token === null) store.removeItem(OPERATOR_TOKEN_KEY);
    else store.setItem(OPERATOR_TOKEN_KEY, token);
    if (refresh === null) store.removeItem(OPERATOR_REFRESH_KEY);
    else store.setItem(OPERATOR_REFRESH_KEY, refresh);
  } catch {
    // best-effort: without it, leaving an assist costs a fresh sign-in rather than losing anything
  }
}

export interface KeptSession {
  token: string | null;
  refresh: string | null;
}

/** What was put aside, and forget it in the same breath — it is taken exactly once. */
export function takeOperatorSession(): KeptSession {
  try {
    const store = storage();
    if (store === null) return { token: null, refresh: null };
    const token = store.getItem(OPERATOR_TOKEN_KEY);
    const refresh = store.getItem(OPERATOR_REFRESH_KEY);
    store.removeItem(OPERATOR_TOKEN_KEY);
    store.removeItem(OPERATOR_REFRESH_KEY);
    return { token, refresh };
  } catch {
    return { token: null, refresh: null };
  }
}

/**
 * True while this tab is holding an operator's own session aside.
 *
 * 🔴 `?.getItem(…) !== null` was wrong and a test caught it: with storage unavailable the optional
 * chain yields `undefined`, and `undefined !== null` is TRUE — so a browser set to block site data
 * would have been told it was holding a session it is not, and offered a "stop assisting" that
 * silently does nothing.
 */
export function hasKeptOperatorSession(): boolean {
  try {
    const kept = storage()?.getItem(OPERATOR_TOKEN_KEY);
    return typeof kept === "string" && kept !== "";
  } catch {
    return false;
  }
}

/** The `act` and `aud` claims of a token, without verifying it — for DRAWING only, never for deciding. */
export interface AssistClaims {
  actingFor: string | null;
  audience: string | null;
  subject: string | null;
  expiresAt: number | null;
}

/**
 * 🔴 Read for the banner, and for nothing else. The claims are already verified — by the Passport that
 * signed them and by every API that receives them — so reading them here is reading a receipt, not
 * making a decision. A UI that decided anything on an unverified payload would be a UI that can be
 * lied to by whoever holds the tab.
 */
export function readAssistClaims(token: string | null): AssistClaims {
  const empty: AssistClaims = { actingFor: null, audience: null, subject: null, expiresAt: null };
  if (token === null) return empty;
  const parts = token.split(".");
  if (parts.length !== 3 || parts[1] === undefined) return empty;
  try {
    const payload = JSON.parse(atob(parts[1].replace(/-/g, "+").replace(/_/g, "/"))) as Record<string, unknown>;
    return {
      actingFor: typeof payload["act"] === "string" ? payload["act"] : null,
      audience: typeof payload["aud"] === "string" ? payload["aud"] : null,
      subject: typeof payload["sub"] === "string" ? payload["sub"] : null,
      expiresAt: typeof payload["exp"] === "number" ? payload["exp"] * 1000 : null,
    };
  } catch {
    return empty;
  }
}
